import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartAPI } from '../services/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutDrawerOpen, setCheckoutDrawerOpen] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);

  const fetchCart = useCallback(async () => {
    if (!user) { setCart(null); return; }
    setLoading(true);
    try {
      const res = await cartAPI.get();
      setCart(res.data);
    } catch {
      setCart(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  // After login, add the pending item to cart
  useEffect(() => {
    if (user && pendingItem) {
      cartAPI.add({ variant_id: pendingItem.variantId, quantity: pendingItem.quantity })
        .then(res => setCart(res.data))
        .catch(() => toast.error('Failed to add item to cart'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const clearPendingItem = () => setPendingItem(null);

  // FIX: Accept productInfo so price/name/label are passed correctly
  // Called from ProductsListPage (no productInfo) and ProductPage (with productInfo via buyNow)
  const addToCart = async (variantId, quantity = 1, productInfo = {}) => {
    if (!user) {
      // FIX: Use productInfo if provided, else show placeholder
      setPendingItem({
        variantId,
        quantity,
        productName: productInfo.productName || 'Selected Product',
        packLabel:   productInfo.packLabel   || '',
        price:       productInfo.price       || 0,
      });
      setCheckoutDrawerOpen(true);
      return false;
    }
    try {
      const res = await cartAPI.add({ variant_id: variantId, quantity });
      setCart(res.data);
      toast.success('Added to cart! 🛒');
      setCartOpen(true);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add to cart');
      return false;
    }
  };

  const buyNow = async (variantId, quantity = 1, productInfo = {}) => {
    if (!user) {
      setPendingItem({
        variantId,
        quantity,
        productName: productInfo.productName || 'Selected Product',
        packLabel:   productInfo.packLabel   || '',
        price:       productInfo.price       || 0,
      });
      setCheckoutDrawerOpen(true);
      return;
    }
    try {
      const res = await cartAPI.add({ variant_id: variantId, quantity });
      setCart(res.data);
      setCheckoutDrawerOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add to cart');
    }
  };

  const updateItem = async (itemId, quantity) => {
    try {
      const res = await cartAPI.updateItem(itemId, { quantity });
      setCart(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update cart');
    }
  };

  const removeItem = async (itemId) => {
    try {
      const res = await cartAPI.removeItem(itemId);
      setCart(res.data);
      toast.success('Item removed');
    } catch {
      toast.error('Failed to remove item');
    }
  };

  const clearCart = async () => {
    try {
      await cartAPI.clear();
      setCart(prev => ({ ...prev, items: [], total_items: 0, total_price: 0 }));
    } catch {}
  };

  const itemCount  = cart?.total_items || 0;
  const totalPrice = cart?.total_price  || 0;

  return (
    <CartContext.Provider value={{
      cart, loading, cartOpen, setCartOpen,
      checkoutDrawerOpen, setCheckoutDrawerOpen,
      pendingItem, clearPendingItem,
      addToCart, buyNow, updateItem, removeItem, clearCart,
      fetchCart, itemCount, totalPrice,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};